import { useMemo, useState } from 'react'

import { PlusIcon } from '@radix-ui/react-icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { zodValidator } from '@tanstack/zod-adapter'
import { EditIcon, Eye, Trash2Icon } from 'lucide-react'
import { z } from 'zod'

import { JobListQuery } from '@/_gql/graphql'

import { gqlFetch } from '@/services/graphql/fetcher'
import { GqlJobDelete } from '@/services/job/gql'
import { queryJobList } from '@/services/job/query'

import { AdminPage } from '@/components/layout/admin/page'
import { Badge } from '@/components/ui/badge'
import { Button, ButtonLoading } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table/data-table'
import { useDataTable } from '@/components/ui/data-table/use-data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

import { toIdr } from '@/lib/utils/number'
import { invalidateQueryKeys } from '@/lib/utils/utils'

import { useFilters } from '@/hooks/use-filter'

const searchParamsSchema = z.object({
  s: z.string().nullish(),
})

export const Route = createFileRoute('/admin-v2/job/vacancy/')({
  validateSearch: zodValidator(searchParamsSchema),
  component: RouteComponent,
})

function RouteComponent() {
  const { filters, setFilters } = useFilters('/admin-v2/job/vacancy/')
  const columns = useMemo(getColumns, [])

  return (
    <AdminPage>
      <AdminPage.Breadcrumb
        items={[['Master Job Listing', '/admin-v2/job/vacancy']]}
      />
      <AdminPage.Header
        title="Master Job Listing"
        action={
          <Button size="sm" asChild>
            <Link from={Route.fullPath} to="/admin-v2/job/vacancy/create">
              <PlusIcon />
              Tambah Job Listing
            </Link>
          </Button>
        }
      />
      <Card>
        <CardContent className="relative space-y-2 p-2">
          <div>
            <Input
              className="max-w-80"
              placeholder="Cari lowongan"
              defaultValue={filters.s || undefined}
              onChange={(e) => {
                setFilters({ s: e.target.value })
              }}
            />
          </div>
          {/* <DataTable table={table} /> */}
        </CardContent>
      </Card>
    </AdminPage>
  )
}

type JobItem = JobListQuery['getJob']['data'][number]
const column = createColumnHelper<JobItem>()

function getColumns() {
  return [
    column.accessor('id', {
      header: 'ID',
    }),
    column.accessor('title', {
      header: 'Judul Pekerjaan',
    }),
    column.accessor('job_skills', {
      header: 'Keterampilan',
      cell: ({ cell }) => {
        const skills = cell.row.original.job_skills?.filter(Boolean)

        if (!skills || !skills.length) return '-'

        return (
          <div className="flex max-w-sm flex-nowrap gap-2 overflow-x-auto">
            {skills.map((s) => (
              <Badge key={s.id} className="font-normal" variant="yes">
                {s.name}
              </Badge>
            ))}
          </div>
        )
      },
    }),
    column.accessor((row) => `${row.min} - ${row.max}`, {
      header: 'Gaji',
      cell: ({ cell }) => {
        const { min, max } = cell.row.original
        return min && max ? `${toIdr(min)} - ${toIdr(max)}` : '-'
      },
    }),
    column.accessor('is_active', {
      header: 'Aktif',
      cell: ({ cell }) => {
        const { is_active } = cell.row.original
        return is_active ? 'Ya' : 'Tidak'
      },
    }),
    column.display({
      id: 'actions',
      header: 'Aksi',
      cell: ({ cell }) => {
        const { id, title } = cell.row.original
        return (
          <div className="flex gap-1.5">
            <Button
              size="icon"
              className="size-8 text-primary-yes"
              variant="ghost"
              asChild
            >
              <Link
                from={Route.fullPath}
                to="/admin-v2/job/vacancy/$id"
                params={{ id: String(id) }}
              >
                <Eye />
              </Link>
            </Button>
            <Button
              size="icon"
              className="size-8 text-primary-yes"
              variant="ghost"
              asChild
            >
              <Link
                from={Route.fullPath}
                to="/admin-v2/job/vacancy/$id/edit"
                params={{ id: String(id) }}
              >
                <EditIcon />
              </Link>
            </Button>
            <RemoveActionButton job={{ id, title }} />
          </div>
        )
      },
    }),
  ]
}

interface DialogProps {
  job: {
    id: number
    title: string
  }
}

function RemoveActionButton({ job }: DialogProps) {
  const [open, setOpen] = useState(false)

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      await gqlFetch({
        query: GqlJobDelete,
        variables: { id: job.id },
      })
      invalidateQueryKeys(['job-list', 'job-detail'])
      setOpen(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" className="size-8 text-primary-yes" variant="ghost">
          <Trash2Icon />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete job</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this job? This action cannot be
            undone.
          </DialogDescription>
          <p className="w-min max-w-full text-nowrap rounded-md bg-slate-200 p-1.5 text-sm font-medium text-slate-700">
            {job.title}
          </p>
        </DialogHeader>
        <DialogFooter>
          <ButtonLoading onClick={() => mutateAsync()} isLoading={isPending}>
            Delete
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
